import { useState } from 'react';
import { NavLink, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  CreditCard,
  Users,
  Layers,
  UserCog,
  Shield,
  Server,
  HeadphonesIcon,
  Lock,
  ChevronLeft,
  ChevronRight,
  LogOut,
  UserCircle,
  BarChart3,
  Settings2,
} from 'lucide-react';
import { useConsoleAuth } from '@/contexts/AuthContext';

interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: 'MAIN',
    items: [
      { label: 'Dashboard', path: '/', icon: LayoutDashboard },
      { label: 'Analytics', path: '/analytics', icon: BarChart3 },
    ],
  },
  {
    title: 'MANAGEMENT',
    items: [
      { label: 'Tenants', path: '/tenants', icon: Building2 },
      { label: 'Billing', path: '/billing', icon: CreditCard },
      { label: 'Users', path: '/users', icon: Users },
      { label: 'Platform', path: '/platform', icon: Layers },
      { label: 'Config', path: '/config', icon: Settings2 },
    ],
  },
  {
    title: 'ADMINISTRATION',
    items: [
      { label: 'Admins', path: '/admins', icon: UserCog },
      { label: 'Roles', path: '/roles', icon: Shield },
      { label: 'System', path: '/system', icon: Server },
      { label: 'Support', path: '/support', icon: HeadphonesIcon },
      { label: 'Security', path: '/security', icon: Lock },
    ],
  },
];

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { admin, logout } = useConsoleAuth();
  const location = useLocation();

  return (
    <aside
      className={`flex flex-col bg-gray-900 text-white transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Branding */}
      <div className="flex h-16 items-center justify-between border-b border-gray-800 px-4">
        {!collapsed && <img src="/logo-black.png" alt="Impactra" className="h-7 brightness-0 invert" />}
        <button
          onClick={() => setCollapsed((prev) => !prev)}
          className="rounded p-1 text-gray-400 hover:bg-gray-800 hover:text-white"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {navSections.map((section) => (
          <div key={section.title} className="mb-4">
            {!collapsed && (
              <p className="mb-1 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                {section.title}
              </p>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const isActive =
                  item.path === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(item.path);
                const Icon = item.icon;

                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.path === '/'}
                      className={`flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? 'border-l-2 border-primary-500 bg-gray-800 text-white'
                          : 'border-l-2 border-transparent text-gray-400 hover:bg-gray-800 hover:text-white'
                      } ${collapsed ? 'justify-center px-0' : ''}`}
                      title={collapsed ? item.label : undefined}
                    >
                      <Icon className="h-5 w-5 shrink-0" />
                      {!collapsed && <span>{item.label}</span>}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Admin info & logout */}
      <div className="border-t border-gray-800 p-4">
        {!collapsed && admin && (
          <p className="mb-2 truncate text-sm font-medium text-gray-300">{admin.firstName} {admin.lastName}</p>
        )}
        <Link
          to="/profile"
          className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-400 hover:bg-gray-800 hover:text-white ${
            collapsed ? 'justify-center' : ''
          }`}
          title="Profile"
        >
          <UserCircle className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Profile</span>}
        </Link>
        <button
          onClick={() => void logout()}
          className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-400 hover:bg-gray-800 hover:text-white ${
            collapsed ? 'justify-center' : ''
          }`}
          title="Logout"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
}
